import { useEffect, useId, useRef, useState } from 'react'
import { ChevronRightIcon, CloseIcon } from './icons'
import {
  activeFilterCount,
  describeActiveFilters,
  EMPTY_FILTERS,
  removeFilter,
  toggleFilter,
  type FacetOption,
  type TyreOfferFilters,
  type TyreSeason,
  type TyreTier,
} from '../lib/tyreOffers'

export interface TyreFilterMenuProps {
  facets: {
    brands: FacetOption<string>[]
    tiers: FacetOption<TyreTier>[]
    seasons: FacetOption<TyreSeason>[]
  }
  filters: TyreOfferFilters
  onChange: (filters: TyreOfferFilters) => void
  /** Result count, shown on the apply button so the effect is visible before closing. */
  resultCount: number
}

/**
 * Filters behind a single button.
 *
 * Three rows of chips — ranges, seasons and a brand for every manufacturer in
 * the results — pushed the tyres themselves off the screen. Collapsing them
 * costs one tap and gives the list the room.
 *
 * Two deliberate choices:
 *
 * - Applied filters stay visible as removable chips outside the menu. A closed
 *   dropdown hides what has been chosen, which is how somebody ends up staring
 *   at three results wondering where everything went.
 * - The panel expands in flow rather than floating. The step renders inside a
 *   scrolling container, and an absolutely positioned menu gets clipped by it.
 */
export function TyreFilterMenu({ facets, filters, onChange, resultCount }: TyreFilterMenuProps) {
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const containerRef = useRef<HTMLDivElement>(null)

  const count = activeFilterCount(filters)
  const applied = describeActiveFilters(filters)

  const hasAnyFacet =
    facets.tiers.length > 0 || facets.seasons.length > 0 || facets.brands.length > 0

  // Escape closes the menu without closing the booking panel behind it.
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      setOpen(false)
    }

    const node = containerRef.current
    node?.addEventListener('keydown', handleKeyDown)
    return () => node?.removeEventListener('keydown', handleKeyDown)
  }, [open])

  if (!hasAnyFacet) return null

  return (
    <div className="ac-filtermenu" ref={containerRef}>
      <div className="ac-filtermenu__bar">
        <button
          type="button"
          className={`ac-filtermenu__trigger${open ? ' ac-filtermenu__trigger--open' : ''}`}
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-controls={panelId}
        >
          <span>Filter</span>
          {count > 0 ? <span className="ac-filtermenu__badge">{count}</span> : null}
          <span className={`ac-filtermenu__chevron${open ? ' ac-filtermenu__chevron--open' : ''}`} aria-hidden="true">
            <ChevronRightIcon size={16} />
          </span>
        </button>

        <span className="ac-filtermenu__count" aria-live="polite">
          {resultCount} {resultCount === 1 ? 'tyre' : 'tyres'}
        </span>
      </div>

      {applied.length > 0 ? (
        <div className="ac-filtermenu__applied">
          {applied.map((chip) => (
            <button
              key={`${chip.facet}-${chip.value}`}
              type="button"
              className="ac-chip ac-chip--on ac-chip--removable"
              onClick={() => onChange(removeFilter(filters, chip.facet, chip.value))}
              aria-label={`Remove ${chip.label} filter`}
            >
              {chip.label}
              <CloseIcon size={13} />
            </button>
          ))}

          <button
            type="button"
            className="ac-btn ac-btn--ghost ac-filtermenu__clear"
            onClick={() => onChange(EMPTY_FILTERS)}
          >
            Clear all
          </button>
        </div>
      ) : null}

      {open ? (
        <div className="ac-filtermenu__panel" id={panelId}>
          <FacetGroup
            legend="Range"
            options={facets.tiers}
            selected={filters.tiers}
            onToggle={(value) => onChange({ ...filters, tiers: toggleFilter(filters.tiers, value) })}
          />
          <FacetGroup
            legend="Season"
            options={facets.seasons}
            selected={filters.seasons}
            onToggle={(value) =>
              onChange({ ...filters, seasons: toggleFilter(filters.seasons, value) })
            }
          />
          <FacetGroup
            legend="Brand"
            options={facets.brands}
            selected={filters.brands}
            onToggle={(value) =>
              onChange({ ...filters, brands: toggleFilter(filters.brands, value) })
            }
          />

          <button
            type="button"
            className="ac-btn ac-btn--primary ac-filtermenu__apply"
            onClick={() => setOpen(false)}
          >
            Show {resultCount} {resultCount === 1 ? 'tyre' : 'tyres'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

interface FacetGroupProps<T extends string> {
  legend: string
  options: FacetOption<T>[]
  selected: T[]
  onToggle: (value: T) => void
}

function FacetGroup<T extends string>({ legend, options, selected, onToggle }: FacetGroupProps<T>) {
  if (options.length === 0) return null

  return (
    <fieldset className="ac-facet">
      <legend className="ac-facet__legend">{legend}</legend>
      <div className="ac-facet__options">
        {options.map((option) => (
          <label key={option.value} className="ac-facet__option">
            <input
              type="checkbox"
              checked={selected.includes(option.value)}
              onChange={() => onToggle(option.value)}
            />
            <span className="ac-facet__label">{option.label}</span>
            <span className="ac-facet__count">{option.count}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}
